/**
 * @fileoverview Full-screen viewer for a rendered Mermaid SVG. Opened from the
 * DiagramRenderer (click the diagram or the expand button). Supports wheel /
 * button zoom and drag-to-pan so dense structure charts can be read in detail.
 * Receives the already-rendered SVG so it never re-parses the source.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Maximize2, Minus, Plus, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";

const MIN_SCALE = 0.25;
const MAX_SCALE = 6;

const toolbarButtonClass =
  "inline-flex h-8 min-w-8 items-center justify-center gap-1 rounded-md border border-border bg-background px-2 text-xs text-foreground hover:bg-accent/30";

export function DiagramLightbox({
  svg,
  title,
  open,
  onOpenChange,
}: {
  svg: string;
  title?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(
    null,
  );
  const viewportRef = useRef<HTMLDivElement>(null);

  // Reset the view each time the lightbox opens.
  useEffect(() => {
    if (open) {
      setScale(1);
      setOffset({ x: 0, y: 0 });
    }
  }, [open]);

  const clamp = (s: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));
  const zoomBy = (factor: number) => setScale((s) => clamp(s * factor));

  // Native (non-passive) wheel listener so we can preventDefault page scroll.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el || !open) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      setScale((s) => clamp(s * factor));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [open]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      drag.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [offset],
  );
  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    setOffset({ x: d.ox + (e.clientX - d.x), y: d.oy + (e.clientY - d.y) });
  }, []);
  const endDrag = useCallback(() => {
    drag.current = null;
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="flex h-[92vh] w-[96vw] max-w-none flex-col gap-0 overflow-hidden p-0"
      >
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2">
          <div className="min-w-0">
            <DialogTitle className="truncate text-sm font-semibold">
              {title ?? "Diagram"}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Scroll to zoom, drag to pan
            </DialogDescription>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              title="Zoom out"
              onClick={() => zoomBy(1 / 1.25)}
              className={toolbarButtonClass}
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <span className="w-12 text-center text-xs tabular-nums text-muted-foreground">
              {Math.round(scale * 100)}%
            </span>
            <button
              type="button"
              title="Zoom in"
              onClick={() => zoomBy(1.25)}
              className={toolbarButtonClass}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              title="Fit to window"
              onClick={() => {
                setScale(1);
                setOffset({ x: 0, y: 0 });
              }}
              className={toolbarButtonClass}
            >
              <Maximize2 className="h-3.5 w-3.5" />
              Fit
            </button>
            <button
              type="button"
              title="Close"
              onClick={() => onOpenChange(false)}
              className={toolbarButtonClass}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        <div
          ref={viewportRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onDoubleClick={() => zoomBy(1.5)}
          className="flex flex-1 cursor-grab select-none items-center justify-center overflow-hidden bg-muted/30 active:cursor-grabbing"
        >
          <div
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
              transformOrigin: "center center",
              transition: drag.current ? "none" : "transform 80ms ease-out",
            }}
            className="flex h-full w-full items-center justify-center p-6 [&_svg]:h-auto [&_svg]:max-h-full [&_svg]:w-auto [&_svg]:max-w-full"
            // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- SVG produced by renderMermaidSvg (mermaid.ts) with securityLevel "strict" and htmlLabels off: labels are HTML-encoded, scripts/foreignObject stripped
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
